import {CSSProperties, FC, SyntheticEvent, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import './OkCancel.less';
import './Buttons.less';
import ButtonGroup from "./ButtonGroup";
import Button from "./Button";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck, faTimes} from '@fortawesome/pro-light-svg-icons';
import {faTrashAlt} from '@fortawesome/pro-solid-svg-icons';
import i18n from '../../i18n/i18n';

export type OkCancelProps = {
	label: string;
	direction?: 'row' | 'column',
	disabled: boolean,
	disabledMessage?: string,
	cancelDisabled?: boolean,
	onSubmit?: (e: SyntheticEvent) => void | Promise<void>,
	onClick?: (e: SyntheticEvent) => void | Promise<void>,
	onCancel?: (e: SyntheticEvent) => void,
	onTop?: boolean,
	position?: 'left' | 'center' | 'right' | 'top' | 'bottom',
	style?: 'circle' | 'square',
	onDelete?: (e: SyntheticEvent) => void,
	deleteDisabled?: boolean,
	useDisabledMessage?: boolean,
	/**
	 * Where the disabledMessage sits relative to {label, buttons}.
	 * - 'left'   — always on the left (wide layouts)
	 * - 'bottom' — always below
	 * - 'auto'   — responsive: left when the container is wide enough, below otherwise
	 *
	 * Responsive mode is driven by a CSS container query on the outer wrapper.
	 * Top-positioned validation bars reserve their measured height in the
	 * positioning parent, so wrapped messages do not overlap its content.
	 */
	messagePosition?: 'left' | 'bottom' | 'auto',
	extraSticky?: boolean,
	justBelow?: boolean,
	/**
	 * Fine-tune the vertical position of the pill in pixels.
	 * Negative values nudge up, positive values nudge down. Applied as a
	 * CSS transform so the offset works even when it pushes the pill
	 * outside the bounds of its parent. Intended for sub-5px adjustments.
	 */
	nudgeY?: number,
	likesEntityType?: string,
	likesEntityId?: string,
	likesAreConfidential?: boolean,
}

/**
 * Simple class to add an Ok/Cancel pair.
 *
 * Optionally can style as round or square(-ish)
 */
const OkCancel: FC<OkCancelProps> = props => {
	const {direction = 'row', position = 'right', messagePosition = 'auto'} = props;

	// Re-entry guard: prevent rapid re-clicks from stacking duplicate save flows.
	// Ref handles the synchronous check (immune to closure-capture races on same-render
	// double-tap); state drives the visual disable so the button visibly grays out.
	const isSavingRef = useRef(false);
	const isMountedRef = useRef(true);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => () => { isMountedRef.current = false; }, []);

	const onClick = useCallback((e: SyntheticEvent) => {
		if (!props.onClick || isSavingRef.current) return;
		isSavingRef.current = true;
		setIsSaving(true);
		// 100ms setTimeout is intentional — gives focused inputs' onBlur a chance to flush
		// their typed value into form state before the save handler reads it.
		setTimeout(async () => {
			try { await props.onClick!(e); }
			catch (err) { console.error(err); }
			finally {
				isSavingRef.current = false;
				if (isMountedRef.current) setIsSaving(false);
			}
		}, 100);
	}, [props]);

	const onSubmit = useCallback((e: SyntheticEvent) => {
		if (!props.onSubmit || isSavingRef.current) return;
		isSavingRef.current = true;
		setIsSaving(true);
		setTimeout(async () => {
			try { await props.onSubmit!(e); }
			catch (err) { console.error(err); }
			finally {
				isSavingRef.current = false;
				if (isMountedRef.current) setIsSaving(false);
			}
		}, 100);
	}, [props]);

	const onCancel = useCallback((e: SyntheticEvent) => {
		if (props.onCancel) setTimeout(() => props.onCancel(e), 100);
	}, [props]);

	const onDelete = useCallback((e: SyntheticEvent) => {
		if (props.onDelete) setTimeout(() => props.onDelete(e), 100);
	}, [props]);

	const deleteButton = props.onDelete ? <>
		<Button id={`DELETE_${props.label}`}
				label={<FontAwesomeIcon icon={faTrashAlt}/>}
				severity='danger'
				type='circle'
				size='medium'
				style={{backgroundColor: '#800020', border: 'none'}}
				disabled={props.deleteDisabled}
				onClick={onDelete}
				title='Delete'
		/>
		<div className='ok_cancel__separator'/>
	</> : null;

	// `location` selects between top/bottom container styling. Top is the default;
	// callers that genuinely want the pill at the bottom (e.g. modal dialogs whose
	// own footer is the natural anchor) pass `onTop={false}` to opt out.
	const location = props.onTop === false ? 'bottom' : 'top';
	const showMessage = !!(props.useDisabledMessage && props.disabledMessage);
	const containerRef = useRef<HTMLDivElement>(null);
	// OWA-423: a top bar showing a validation message reserves its height as
	// padding-top on its positioning parent, so the message never covers the
	// card's first row. The reservation only grows: once a message has appeared
	// it keeps the largest measured space until the bar unmounts (the card
	// closes), so fields do not jump while the user types and the message
	// toggles (precedent 16). Assumes one active top bar per positioning parent
	// and no other code managing that parent's inline padding-top.
	const reservationRef = useRef<{
		parent: HTMLElement, originalPadding: string, originalPriority: string, padding: number,
	} | null>(null);
	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container || !showMessage || location !== 'top' || props.justBelow) return;
		const parent = container.offsetParent;
		if (!(parent instanceof HTMLElement)) return;
		let reservation = reservationRef.current;
		if (reservation?.parent !== parent) {
			if (reservation) reservation.parent.style.setProperty('padding-top', reservation.originalPadding, reservation.originalPriority);
			reservation = reservationRef.current = {
				parent,
				originalPadding: parent.style.paddingTop,
				originalPriority: parent.style.getPropertyPriority('padding-top'),
				padding: parseFloat(getComputedStyle(parent).paddingTop) || 0,
			};
		}
		const held = reservation;
		const reserveSpace = () => {
			// Layout coordinates inside the offsetParent: unaffected by its scroll position.
			const padding = Math.ceil(container.offsetTop + container.offsetHeight + 4);
			if (padding <= held.padding) return;
			held.padding = padding;
			parent.style.setProperty('padding-top', `${padding}px`, held.originalPriority);
		};
		reserveSpace();
		const observer = new ResizeObserver(reserveSpace);
		observer.observe(container);
		return () => observer.disconnect();
	}, [showMessage, location, props.justBelow, props.extraSticky, props.nudgeY]);
	useLayoutEffect(() => () => {
		const reservation = reservationRef.current;
		if (reservation) reservation.parent.style.setProperty('padding-top', reservation.originalPadding, reservation.originalPriority);
		reservationRef.current = null;
	}, []);

	const containerClasses = [
		'ok_cancel__container',
		`ok_cancel__container--${location}`,
		`ok_cancel__container--msg-${messagePosition}`,
		props.justBelow ? 'ok_cancel__container--just-below' : '',
	].filter(Boolean).join(' ');

	const containerStyle: CSSProperties = {zIndex: 99};
	if (props.nudgeY) containerStyle.transform = `translateY(${props.nudgeY}px)`;

	const result = (
		<div ref={containerRef} className={containerClasses} style={containerStyle}>
			<div className='ok_cancel'>
				{deleteButton}
				<div className='ok_cancel__label'>{props.label}</div>
				<ButtonGroup direction={direction} position={position}>

					{/* On Cancel */}
					{props.onCancel && <Button id={`CANCEL_${props.label}`}
											   label={<FontAwesomeIcon icon={faTimes}/>}
											   severity='danger'
											   type='circle'
											   size='medium'
											   disabled={props.cancelDisabled}
											   onClick={onCancel}
											   title={i18n.cs('cancel')}
					/>}

					{/* On Ok/Submit */}
					{(props.onClick || props.onSubmit) && <Button id={`OK_${props.label}`}
																  label={<FontAwesomeIcon icon={faCheck}/>}
																  severity='success'
																  type='circle'
																  size='medium'
																  disabled={props.disabled || isSaving}
																  onSubmit={props.onSubmit && onSubmit}
																  onClick={props.onClick && onClick}
																  title={i18n.cs('submit')}
																  useDelay={true}
					/>}
				</ButtonGroup>
				{showMessage && <div className='ok_cancel__disabled-message'>{props.disabledMessage}</div>}
			</div>
		</div>
	);

	if (props.extraSticky) return <div
		className={`ok_cancel_wrapper ok_cancel_wrapper--${location}`}>{result}</div>;
	else return result;
}

export default OkCancel;
